import type { Result } from "@pdfking/errors"
import { Failure, ResultMap, Success } from "@pdfking/errors"
import { ToNumber } from "@pdfking/math"
import { NullQ } from "@pdfking/structs"

import type { QuantityError } from "#public"
import type { Math } from "#public"
import type { QuantitySpec } from "#public"

import { Operation } from "../types/Operation"
import { Convert } from "./Convert"

// A measure literal (number array + optional unit), an operator / paren, or a
// bare unit (applied as a postfix to a parenthesized group, e.g. `(1+2)cm`).
const TOKEN_RE = /([+\-*/()]|[\d.,]+(?:x[\d.,]+)*[a-z_¹²³"%]*|[a-z_¹²³"%]+)/giy
// Split a literal into its `x`-joined magnitudes and its (optional) unit.
const LITERAL_RE = /^([\d.,]+(?:x[\d.,]+)*)([a-z_¹²³"%]*)$/
// A standalone unit token.
const UNIT_RE = /^[a-z_¹²³"%]+$/

// One magnitude: either plain digits, or thousands-grouped 3s (capturing the
// thousands separator), then an optional decimal (capturing its separator).
// `1.000` matches plain `1` + decimal `.000`, so it stays 1 (not 1000).
const NUMBER_RE = /^(?:\d+|\d{1,3}(?:([.,])\d{3})+)(?:([.,])\d+)?$/

// Parse a magnitude, rejecting malformed input or a thousands separator equal to
// the decimal one. Normalizes to a plain `.`-decimal before `Number`.
const parseNumber = (raw: string): number | undefined => {
  const match = NUMBER_RE.exec(raw)
  if (!match) return undefined
  const [, thousands, decimal] = match
  if (thousands && thousands === decimal) return undefined
  let normalized = raw
  if (thousands) normalized = normalized.split(thousands).join("")
  if (decimal && decimal !== ".") normalized = normalized.split(decimal).join(".")
  return ToNumber(normalized)
}

// Cycle a short vector up to `length` (e.g. `[1]` → `[1,1,1,1]`).
const fit = <T>(values: T[], length: number): T[] =>
  Array.from({ length }, (_, i) => values[i % values.length] as T)

// Parse an arithmetic expression of measure literals (`2cm + 3 * (4mm - 1mm)`)
// into a `Math` tree, or fail with a `QuantityError`.
//
// `exp` (unit power) per leaf: a literal with a unit is `exp: 1` (needs `spec`,
// else the unit is an error); a bare number is `exp: 0`, EXCEPT a lone top-level
// number with a `spec` — there `20` auto-takes the spec's input unit (`20cm`).
// Inside any compound expression (`complex`) a bare number stays `exp: 0`, so to
// get units in `20 * 2` you must write them: `20cm * 2`.
export const StringToMath = <T extends number>(
  input: string,
  spec?: QuantitySpec<T>,
  length?: number
): Result<Math, QuantityError> => {
  // every error carries the spec it came from (when any) so a consumer can
  // format units/values — attach it here at the source, not re-injected later.
  const source = input.toLowerCase().replace(/\s+/g, "")
  const tokens: string[] = []
  let pos = 0
  while (pos < source.length) {
    TOKEN_RE.lastIndex = pos
    const match = TOKEN_RE.exec(source)
    if (!match || match.index !== pos)
      return Failure("QUANTITY_PARSE_ERROR", { input, quantity: spec })
    tokens.push(match[1])
    pos = TOKEN_RE.lastIndex
  }
  // a single literal is the only non-`complex` case: there a bare number adopts
  // the spec's input unit.
  const complex = tokens.length !== 1

  let i = 0
  const peek = (): string => tokens[i] ?? ""
  const parseError = (): Result<Math, QuantityError> =>
    Failure("QUANTITY_PARSE_ERROR", { input, quantity: spec })
  const ok = (node: Math): Result<Math, QuantityError> => Success(node)

  const coerce = (values: T[]): Result<T[], QuantityError> => {
    if (NullQ(length) || values.length === length) return Success(values)
    if (values.length > 0 && values.length < length) return Success(fit(values, length))
    return Failure("QUANTITY_LENGTH_ERROR", {
      expected: length,
      got: values.length,
      input,
      quantity: spec,
    })
  }

  // a measure literal → a QUANTITY leaf, converting magnitudes to base units
  const leaf = (token: string): Result<Math, QuantityError> => {
    const match = LITERAL_RE.exec(token)
    if (!match) return parseError()
    const [, array, written] = match
    const parsed = array.split("x").map(parseNumber)
    if (parsed.some(NullQ)) return parseError()
    const magnitudes = parsed as number[]

    // exp 0 (bare number) unless a unit is written or it's a lone number + spec
    if (!written && (complex || !spec))
      return ResultMap(coerce(magnitudes as T[]), values =>
        ok({ enum: Operation.NUM, exp: 0, vars: values })
      )

    // no spec → no unit system at all, so a written unit is just unparseable
    if (!spec) return parseError()
    const unit = written || spec.input_unit
    if (NullQ(spec.units[unit]))
      return Failure("QUANTITY_UNIT_ERROR", {
        unit,
        example: spec.input_unit,
        input,
        quantity: spec,
      })
    return ResultMap(
      coerce(magnitudes.map(n => Convert(n, spec, unit, spec.standard_unit) as T)),
      values => ok({ enum: Operation.NUM, exp: 1, vars: values })
    )
  }

  // Apply a postfix unit to a group: `(expr) cm` ⇒ `(expr) * 1cm`, so a
  // dimensionless result becomes exp 1 (and an already-unit one overflows).
  const applyUnit = (base: Math, unit: string): Result<Math, QuantityError> => {
    if (!spec) return parseError()
    if (NullQ(spec.units[unit]))
      return Failure("QUANTITY_UNIT_ERROR", {
        unit,
        example: spec.input_unit,
        input,
        quantity: spec,
      })
    const one = Convert(1, spec, unit, spec.standard_unit) as T
    return ResultMap(coerce([one]), values =>
      ok({
        enum: Operation.MUL,
        left: base,
        right: { enum: Operation.NUM, exp: 1, vars: values },
      } as Math)
    )
  }

  // factor → '(' expr ')' unit? | '-' factor | literal
  const factor = (): Result<Math, QuantityError> => {
    const token = peek()
    if (token === "(") {
      i++
      const group = ResultMap(expr(), inner => {
        if (peek() !== ")") return parseError()
        i++
        return ok(inner)
      })
      if (UNIT_RE.test(peek())) return ResultMap(group, b => applyUnit(b, tokens[i++] ?? ""))
      return group
    }
    if (token === "-") {
      i++
      return ResultMap(factor(), value => ok({ enum: Operation.NEG, var: value }))
    }
    if (/^[+*/)]?$/.test(token)) return parseError()
    i++
    return leaf(token)
  }

  // Left-associative fold of a binary level: `next (op next)*`.
  const fold = (
    next: () => Result<Math, QuantityError>,
    ops: Partial<Record<string, Operation>>
  ): Result<Math, QuantityError> => {
    let acc = next()
    while (!acc.error && ops[peek()]) {
      const op = ops[tokens[i++] ?? ""] as Operation
      acc = ResultMap(acc, left =>
        ResultMap(next(), right => ok({ enum: op, left, right } as Math))
      )
    }
    return acc
  }

  // term → factor (('*' | '/') factor)* ; expr → term (('+' | '-') term)*
  const term = (): Result<Math, QuantityError> =>
    fold(factor, { "*": Operation.MUL, "/": Operation.DIV })
  const expr = (): Result<Math, QuantityError> =>
    fold(term, { "+": Operation.ADD, "-": Operation.SUB })

  return ResultMap(expr(), node => (i === tokens.length ? ok(node) : parseError()))
}
